import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Search,
  Tags,
  ArrowLeft,
  PlusCircle,
  Pencil,
  Trash2,
  X,
  Users,
  UserPlus,
  CheckCircle2,
  Link2,
  UserMinus,
  Layers,
} from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import {
  CategoriaItem,
  SocioDeCategoria,
  ServicioDeCategoria,
  actualizarCategoria,
  asignarServiciosACategoria,
  asignarSocioACategoria,
  crearCategoria,
  eliminarCategoria,
  obtenerCategorias,
  obtenerServiciosDeCategoria,
  obtenerSociosDeCategoria,
  quitarSocioDeCategoria,
} from "../services/categoriasService.ts";
import socioService from "../services/socioService.tsx";
import { useAdminRole } from "../hooks/useAdminRole.ts";

type ModalEstado = {
  tipo: "crear" | "editar" | "socios" | "servicios" | "borrar";
  categoria?: CategoriaItem;
} | null;

export default function CategoriasGestor() {
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAdminRole();
  const [categorias, setCategorias] = useState<CategoriaItem[]>([]);
  const [filtro, setFiltro] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalEstado>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const data = await obtenerCategorias(token);
      setCategorias(data);
    } catch (err: any) {
      setError(err?.message || "No se pudieron cargar las categorías.");
    } finally {
      setCargando(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const filtradas = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return categorias;
    return categorias.filter(
      (c) =>
        (c.nombre || "").toLowerCase().includes(q) ||
        (c.descripcion || "").toLowerCase().includes(q)
    );
  }, [categorias, filtro]);

  if (!isAdmin) return <Navigate to="/inicio" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-10 space-y-6">
        <Link
          to="/inicio"
          className="mb-6 flex items-center gap-1 text-sm font-medium text-slate-600 transition hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> Volver al inicio
        </Link>

        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Administración</p>
            <h1 className="text-2xl font-bold text-slate-900">Categorías de socios</h1>
            <p className="mt-1 text-sm text-slate-500">
              Creá categorías, asigná socios y asigná servicios del catálogo por categoría.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm">
              {categorias.length} categoría{categorias.length !== 1 ? "s" : ""}
            </div>
            <button
              type="button"
              onClick={() => setModal({ tipo: "crear" })}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
            >
              <PlusCircle className="h-4 w-4" />
              Crear categoría
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {filtradas.length} categoría{filtradas.length !== 1 ? "s" : ""}
              </p>
              <p className="text-xs text-slate-500">Seleccioná una categoría para gestionar socios y servicios.</p>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
              <Search className="h-4 w-4 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar por nombre"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                className="w-52 bg-transparent text-sm text-slate-800 focus:outline-none"
              />
            </div>
          </div>

          {mensaje && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">{mensaje}</div>}
          {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">{error}</div>}

          {cargando ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm animate-pulse">
                  <div className="h-4 w-28 rounded bg-slate-200" />
                  <div className="mt-2 h-6 w-40 rounded bg-slate-200" />
                  <div className="mt-3 h-10 w-full rounded bg-slate-200" />
                </div>
              ))}
            </div>
          ) : !filtradas.length ? (
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
              No hay categorías todavía. Creá la primera para empezar a agrupar socios.
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {filtradas.map((categoria) => {
                const id = categoria.categoria_id ?? categoria.id;
                return (
                  <div
                    key={id}
                    className="flex h-full flex-col justify-between rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500 text-white shadow">
                            <Tags className="h-5 w-5" strokeWidth={2.2} />
                          </div>
                          <p className="text-sm font-semibold text-slate-900">{categoria.nombre}</p>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-slate-600">
                        {categoria.descripcion || "Sin descripción"}
                      </p>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                      <div className="rounded-xl bg-slate-50 px-2 py-3">
                        <p className="text-xl font-semibold text-slate-900">{categoria.socios ?? 0}</p>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Socios</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 px-2 py-3">
                        <p className="text-xl font-semibold text-slate-900">{categoria.servicios ?? 0}</p>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Servicios</p>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setModal({ tipo: "socios", categoria })}
                        className="inline-flex items-center gap-1.5 rounded-full bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-sky-600"
                      >
                        <Users className="h-3.5 w-3.5" />
                        Socios
                      </button>
                      <button
                        type="button"
                        onClick={() => setModal({ tipo: "servicios", categoria })}
                        className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                      >
                        <Link2 className="h-3.5 w-3.5" />
                        Asignar servicios
                      </button>
                      <div className="ml-auto flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setModal({ tipo: "editar", categoria })}
                          className="inline-flex items-center justify-center rounded-full bg-slate-100 p-2 text-slate-600 shadow-sm transition hover:bg-slate-200"
                          title="Editar categoría"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setModal({ tipo: "borrar", categoria })}
                          className="inline-flex items-center justify-center rounded-full bg-rose-50 p-2 text-rose-600 shadow-sm transition hover:bg-rose-100"
                          title="Dar de baja categoría"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {modal?.tipo === "crear" && (
        <CrearEditarModal
          title="Crear categoría"
          onClose={() => { setModal(null); }}
          onGuardar={async (payload) => {
            const token = await getAccessTokenSilently();
            await crearCategoria(payload, token);
            setModal(null);
            setMensaje(`Categoría "${payload.nombre}" creada.`);
            await cargar();
          }}
        />
      )}

      {modal?.tipo === "editar" && modal.categoria && (
        <CrearEditarModal
          title="Editar categoría"
          nombreInicial={modal.categoria.nombre}
          descripcionInicial={modal.categoria.descripcion ?? ""}
          onClose={() => { setModal(null); }}
          onGuardar={async (payload) => {
            const token = await getAccessTokenSilently();
            await actualizarCategoria(modal.categoria!.categoria_id ?? modal.categoria!.id!, payload, token);
            setModal(null);
            setMensaje(`Categoría "${payload.nombre || modal.categoria!.nombre}" actualizada.`);
            await cargar();
          }}
        />
      )}

      {modal?.tipo === "borrar" && modal.categoria && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                <Trash2 className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900">Dar de baja categoría</h3>
                <p className="mt-1 text-sm text-slate-600">
                  ¿Dar de baja la categoría &quot;{modal.categoria.nombre}&quot;? Quedará inactiva y dejará de
                  listarse, pero no se modifican las asignaciones de socios ni servicios existentes.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModal(null)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setModal(null)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const token = await getAccessTokenSilently();
                    await eliminarCategoria(modal.categoria!.categoria_id ?? modal.categoria!.id!, token);
                    setModal(null);
                    setMensaje(`Categoría "${modal.categoria!.nombre}" dada de baja.`);
                    await cargar();
                  } catch (err: any) {
                    setError(err?.response?.data?.message || err?.message || "No se pudo dar de baja la categoría.");
                  }
                }}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700"
              >
                Dar de baja
              </button>
            </div>
          </div>
        </div>
      )}

      {modal?.tipo === "socios" && modal.categoria && (
        <SociosModal
          categoria={modal.categoria}
          getAccessTokenSilently={getAccessTokenSilently}
          onClose={() => { setModal(null); }}
          onCambio={async (msg) => {
            setMensaje(msg);
            await cargar();
          }}
        />
      )}

      {modal?.tipo === "servicios" && modal.categoria && (
        <ServiciosModal
          categoria={modal.categoria}
          getAccessTokenSilently={getAccessTokenSilently}
          onClose={() => { setModal(null); }}
          onCambio={async (msg) => {
            setMensaje(msg);
            await cargar();
          }}
        />
      )}
    </div>
  );
}

function CrearEditarModal({
  title,
  nombreInicial = "",
  descripcionInicial = "",
  onClose,
  onGuardar,
}: {
  title: string;
  nombreInicial?: string;
  descripcionInicial?: string;
  onClose: () => void;
  onGuardar: (payload: { nombre: string; descripcion?: string }) => Promise<void>;
}) {
  const [nombre, setNombre] = useState(nombreInicial);
  const [descripcion, setDescripcion] = useState(descripcionInicial);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) {
      setError("Ingresá un nombre para la categoría.");
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      await onGuardar({ nombre: nombre.trim(), descripcion: descripcion.trim() || undefined });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "No se pudo guardar la categoría.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Categorías</p>
            <h2 className="text-xl font-bold text-slate-900">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
          <label className="text-sm font-semibold text-slate-700">
            Nombre
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              placeholder="Ej: Empresas, Comercios, Jubilados..."
              required
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Descripción
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className="mt-1 min-h-[80px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              placeholder="Opcional: breve descripción."
            />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={guardando}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
            >
              <CheckCircle2 className="h-4 w-4" />
              {guardando ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SociosModal({
  categoria,
  getAccessTokenSilently,
  onClose,
  onCambio,
}: {
  categoria: CategoriaItem;
  getAccessTokenSilently: () => Promise<string>;
  onClose: () => void;
  onCambio: (msg: string) => Promise<void>;
}) {
  const id = categoria.categoria_id ?? categoria.id!;
  const [socios, setSocios] = useState<SocioDeCategoria[]>([]);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [termino, setTermino] = useState("");
  const [resultados, setResultados] = useState<any[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargarSocios = useCallback(async () => {
    setCargandoLista(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const data = await obtenerSociosDeCategoria(id, token);
      setSocios(data);
    } catch (err: any) {
      setError(err?.message || "No se pudieron cargar los socios.");
    } finally {
      setCargandoLista(false);
    }
  }, [id, getAccessTokenSilently]);

  useEffect(() => {
    cargarSocios();
  }, [cargarSocios]);

  const buscar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!termino.trim()) return;
    setBuscando(true);
    setError(null);
    try {
      const data = await socioService.searchSocios(termino, getAccessTokenSilently);
      setResultados(data.slice(0, 20));
    } catch (err: any) {
      setError(err?.message || "No se pudieron buscar socios.");
    } finally {
      setBuscando(false);
    }
  };

  const agregar = async (socioId: number | string) => {
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await asignarSocioACategoria(id, socioId, token);
      setResultados(null);
      setTermino("");
      await cargarSocios();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "No se pudo asignar el socio.");
    }
  };

  const quitar = async (socioId: number | string) => {
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await quitarSocioDeCategoria(id, socioId, token);
      await cargarSocios();
      await onCambio("Socio removido de la categoría.");
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "No se pudo remover el socio.");
    }
  };

  const yaAsignado = (socioId: number | string) =>
    socios.some((s) => String(s.socio_id) === String(socioId));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Categoría</p>
              <h2 className="text-xl font-bold text-slate-900">{categoria.nombre}</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {error && <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

        <form onSubmit={buscar} className="mt-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={termino}
              onChange={(e) => setTermino(e.target.value)}
              placeholder="Buscar socio por nombre, DNI o ID para agregar"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-100"
            />
          </div>
          <button
            type="submit"
            disabled={buscando || !termino.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:opacity-60"
          >
            <UserPlus className="h-4 w-4" />
            {buscando ? "Buscando..." : "Buscar"}
          </button>
        </form>

        {resultados && (
          <div className="mt-3 max-h-44 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
            {resultados.length === 0 ? (
              <p className="px-2 py-2 text-sm text-slate-500">Sin resultados.</p>
            ) : (
              resultados.map((s) => (
                <div
                  key={s.socio_id}
                  className="flex items-center justify-between rounded-lg px-2 py-1.5 hover:bg-slate-100"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      {s.socio_nombre} {s.socio_apellido ?? ""}
                    </p>
                    <p className="text-xs text-slate-500">
                      ID {s.socio_id} · {s.socio_dni || "—"}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={yaAsignado(s.socio_id)}
                    onClick={() => agregar(s.socio_id)}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition ${
                      yaAsignado(s.socio_id)
                        ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                        : "bg-emerald-600 text-white hover:bg-emerald-700"
                    }`}
                  >
                    {yaAsignado(s.socio_id) ? "Ya asignado" : "Agregar"}
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        <div className="mt-4 flex-1 overflow-y-auto">
          {cargandoLista ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />
              ))}
            </div>
          ) : socios.length === 0 ? (
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
              Esta categoría no tiene socios asignados todavía.
            </p>
          ) : (
            <div className="space-y-2">
              {socios.map((s) => (
                <div
                  key={s.socio_id}
                  className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      {s.socio_nombre} {s.socio_apellido ?? ""}
                    </p>
                    <p className="text-xs text-slate-500">
                      ID {s.socio_id} · {s.socio_dni || "—"} · {s.socio_mail || "Sin mail"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => quitar(s.socio_id)}
                    className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-100"
                  >
                    <UserMinus className="h-3.5 w-3.5" />
                    Quitar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function ServiciosModal({
  categoria,
  getAccessTokenSilently,
  onClose,
  onCambio,
}: {
  categoria: CategoriaItem;
  getAccessTokenSilently: () => Promise<string>;
  onClose: () => void;
  onCambio: (msg: string) => Promise<void>;
}) {
  const id = categoria.categoria_id ?? categoria.id!;
  const [servicios, setServicios] = useState<ServicioDeCategoria[]>([]);
  const [totalSocios, setTotalSocios] = useState(0);
  const [seleccionados, setSeleccionados] = useState<number[]>([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const data = await obtenerServiciosDeCategoria(id, token);
      setServicios(Array.isArray(data?.servicios) ? data.servicios : []);
      setTotalSocios(Number(data?.total_socios ?? 0));
    } catch (err: any) {
      setError(err?.message || "No se pudieron cargar los servicios.");
    } finally {
      setCargando(false);
    }
  }, [id, getAccessTokenSilently]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const marcarFaltantes = () => {
    const faltantes = servicios.filter((s) => Number(s.asignados ?? 0) < totalSocios).map((s) => s.servicio_id);
    setSeleccionados((prev) => Array.from(new Set([...prev, ...faltantes])));
  };

  const toggle = (servicioId: number) => {
    setSeleccionados((prev) =>
      prev.includes(servicioId) ? prev.filter((x) => x !== servicioId) : [...prev, servicioId]
    );
  };

  const guardar = async () => {
    if (!seleccionados.length) {
      setError("Seleccioná al menos un servicio.");
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await asignarServiciosACategoria(id, { servicios: seleccionados }, token);
      await onCambio(
        `Servicios asignados: ${res?.asignados ?? 0} nuevos · ${res?.yaAsignados ?? 0} ya existentes.`
      );
      setSeleccionados([]);
      await cargar();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "No se pudieron asignar los servicios.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
              <Link2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Asignar servicios · {categoria.nombre}</p>
              <h2 className="text-xl font-bold text-slate-900">
                {totalSocios} socio{totalSocios !== 1 ? "s" : ""} en la categoría
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <p className="text-xs text-slate-500">
            Marcá los servicios a asignar a todos los socios de esta categoría. Los que ya están asignados no se
            duplican.
          </p>
          <button
            type="button"
            onClick={marcarFaltantes}
            className="shrink-0 rounded-full border border-indigo-200 px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
          >
            Marcar faltantes
          </button>
        </div>

        {error && <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

        <div className="mt-4 flex-1 overflow-y-auto">
          {cargando ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />
              ))}
            </div>
          ) : servicios.length === 0 ? (
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
              No hay servicios activos en el catálogo.
            </p>
          ) : (
            <div className="space-y-2">
              {servicios.map((s) => {
                const asignados = Number(s.asignados ?? 0);
                const completo = totalSocios > 0 && asignados >= totalSocios;
                return (
                  <label
                    key={s.servicio_id}
                    className={`flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2 transition ${
                      seleccionados.includes(s.servicio_id)
                        ? "border-indigo-400 bg-indigo-50"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={seleccionados.includes(s.servicio_id)}
                        onChange={() => toggle(s.servicio_id)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex items-center gap-2">
                        <Layers className="h-4 w-4 text-slate-400" />
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{s.nombre}</p>
                          <p className="text-xs text-slate-500">{s.categoria || "Servicio"}</p>
                        </div>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                        completo ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {completo && <CheckCircle2 className="h-3 w-3" />}
                      {asignados}/{totalSocios}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <p className="text-sm font-semibold text-slate-600">
            {seleccionados.length} servicio{seleccionados.length !== 1 ? "s" : ""} seleccionado
            {seleccionados.length !== 1 ? "s" : ""}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cerrar
            </button>
            <button
              type="button"
              onClick={guardar}
              disabled={guardando || !seleccionados.length}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-60"
            >
              <CheckCircle2 className="h-4 w-4" />
              {guardando ? "Asignando..." : "Asignar servicios"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}